// Route paths - Single source of truth for all routes
export const ROUTES = {
  // Public routes (authentication)
  LOGIN: '/',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ACCEPT_INVITATION: '/accept-invitation',

  // Team selection (requires auth but not team context)
  SELECT_TEAM: '/select-team',

  // Error pages
  FORBIDDEN: '/forbidden',

  // Protected routes (authenticated users with team context)
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',
  TEAMS: '/teams',
  TEAM_DETAILS: '/teams/:teamId',
  TEMPLATES: '/templates',
  CAMPAIGNS: '/campaigns',
  AGENTS: '/agents',
  WORKFLOW_BUILDER: '/workflow-builder',
  WORKFLOW_TEMPLATES: '/workflow-templates',
  MERMAID_DIAGRAM: '/mermaid-diagram',
  CHANNELS: '/channels',
  CHANNEL_DETAILS: '/channels/:channelId',
  BILLING: '/billing',
  CONTACTS: '/contacts',
  INBOX: '/inbox',
  ANALYTICS: '/analytics',
  LIBRARY: '/library',
  FREEFLOW_AGENT_PLAYGROUND: '/freeflow-agent-playground',
  WIDGET_TEST_PLAYGROUND: '/widget-test-playground',
} as const

// Route metadata for better organization
export const ROUTE_CONFIG = {
  [ROUTES.LOGIN]: {
    title: 'Login',
    description: 'Sign in to your account',
    isPublic: true,
    requiresAuth: false,
  },
  [ROUTES.FORGOT_PASSWORD]: {
    title: 'Forgot Password',
    description: 'Reset your password',
    isPublic: true,
    requiresAuth: false,
  },
  [ROUTES.RESET_PASSWORD]: {
    title: 'Reset Password',
    description: 'Reset your password',
    isPublic: true,
    requiresAuth: false,
  },
  [ROUTES.ACCEPT_INVITATION]: {
    title: 'Accept Invitation',
    description: 'Accept team invitation and create your account',
    isPublic: true,
    requiresAuth: false,
  },
  [ROUTES.SELECT_TEAM]: {
    title: 'Select Team',
    description: 'Choose a team to continue',
    isPublic: false,
    requiresAuth: true,
    requiresTeamContext: false,
  },
  [ROUTES.FORBIDDEN]: {
    title: 'Access Denied',
    description: 'You do not have permission to access this resource',
    isPublic: false,
    requiresAuth: true,
    requiresTeamContext: false,
  },
  [ROUTES.DASHBOARD]: {
    title: 'Dashboard',
    description: 'Your dashboard',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.PROFILE]: {
    title: 'Profile',
    description: 'Your profile',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.SETTINGS]: {
    title: 'Settings',
    description: 'Application settings',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.TEAMS]: {
    title: 'Teams',
    description: 'Manage your business profile and team members',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.TEAM_DETAILS]: {
    title: 'Team Details',
    description: 'View team details and members',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.TEMPLATES]: {
    title: 'Templates',
    description: 'Manage your WhatsApp message templates',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.CAMPAIGNS]: {
    title: 'Campaigns',
    description: 'Organize and manage your marketing campaigns',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.AGENTS]: {
    title: 'Agents',
    description: 'Manage your AI agents and automation workflows',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.WORKFLOW_BUILDER]: {
    title: 'Workflow Builder',
    description: 'Design and configure agent conversation flows',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.WORKFLOW_TEMPLATES]: {
    title: 'Workflow Templates',
    description: 'Select from pre-built agent templates for quick setup',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.MERMAID_DIAGRAM]: {
    title: 'Mermaid Diagram',
    description: 'Preview and confirm generated workflow diagram',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.CHANNELS]: {
    title: 'Channels',
    description: 'Manage your communication channels and integrations',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.CHANNEL_DETAILS]: {
    title: 'Channel Details',
    description: 'Configure channel providers and settings',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.BILLING]: {
    title: 'Billing',
    description: 'Manage your credit balance and billing information',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.CONTACTS]: {
    title: 'Contacts',
    description: 'Manage your contacts and customer information',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.INBOX]: {
    title: 'Inbox',
    description: 'View and respond to customer messages',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.ANALYTICS]: {
    title: 'Analytics',
    description: 'View insights and performance metrics',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.LIBRARY]: {
    title: 'Library',
    description: 'Manage your media files and documents',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.FREEFLOW_AGENT_PLAYGROUND]: {
    title: 'Freeflow Agent Playground',
    description: 'Configure and test your freeflow AI agent',
    isPublic: false,
    requiresAuth: true,
  },
  [ROUTES.WIDGET_TEST_PLAYGROUND]: {
    title: 'Widget Test Playground',
    description: 'Test your webchat widget in a live environment',
    isPublic: false,
    requiresAuth: true,
  },
} as const

// Helper function to get route config
export const getRouteConfig = (path: string) => {
  return ROUTE_CONFIG[path as keyof typeof ROUTE_CONFIG]
}

// Helper function to check if route requires authentication
export const isProtectedRoute = (path: string): boolean => {
  const config = getRouteConfig(path)
  return config?.requiresAuth ?? false
}

// Helper function to check if route is public
export const isPublicRoute = (path: string): boolean => {
  const config = getRouteConfig(path)
  return config?.isPublic ?? false
}